import {
  Group,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { AuctionSettings } from "./api";

/**
 * The four settings that make a lot an auction (spec §7.2, §10.5, M1.1) — three of them on
 * screen under sudden death, where the fourth does nothing. See the counter-bid field below.
 *
 * One component for both places a host reaches them — the builder, where a lot is created, and
 * the row, where they can still be changed until the lot opens (`R-AUC-8`). Two copies of four
 * inputs is two places for a default to drift, and the defaults here are what a viewer will be
 * shown (`R-AUC-13`).
 *
 * ## Close mode is a segmented control, not a select
 *
 * Two options, both of which the host has to *read* — spec §10.5 is emphatic that a bidder's
 * behaviour in the final seconds is completely different under the two, and `R-AUC-9` makes
 * displaying it to viewers a requirement. A dropdown hides the alternative behind a click; a
 * segmented control puts both words on screen with the chosen one lit, and the description
 * underneath says what the choice does rather than what it is called.
 *
 * ## Nothing here validates
 *
 * The bounds are `auctionProblems` in `packages/core`, enforced by the route. Repeating them as
 * client-side checks would give two answers to one question and the wrong one would be the one
 * without the starting price in front of it — the increment's ceiling is judged against the
 * price, which lives in a different form. `min` and `max` on the inputs stop a wild value being
 * typed; the refusal is the server's.
 */
export function AuctionFields({
  value,
  onChange,
  disabled,
}: {
  value: AuctionSettings;
  onChange: (settings: AuctionSettings) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Stack gap="xs">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput
          size="xs"
          label={t("lots.auction.incrementLabel")}
          description={t("lots.auction.incrementHelp")}
          value={value.minIncrement}
          onChange={(event) =>
            onChange({ ...value, minIncrement: event.currentTarget.value })
          }
          disabled={disabled}
          w={130}
        />
        <NumberInput
          size="xs"
          label={t("lots.auction.durationLabel")}
          description={t("lots.auction.durationHelp")}
          value={value.durationSeconds}
          min={5}
          max={1800}
          allowDecimal={false}
          suffix="s"
          onChange={(next) =>
            onChange({ ...value, durationSeconds: Number(next) || 0 })
          }
          disabled={disabled}
          w={130}
        />
        {/*
          **Hidden under sudden death, where it does nothing.** The engine skips the whole
          extension branch for that mode — `if lot["closeMode"] ~= "sudden"` in `auction/bid.ts` is
          the only place `counterBidMs` is read — so the number is a setting with no effect, sitting
          beside three that have one, and showing it — even dimmed — says nothing about it having
          stopped mattering.

          **The value is kept rather than cleared.** `R-AUC-8` lets the mode change right up until
          the lot opens, so a host who tries sudden death and goes back finds the number they chose
          rather than a reset default. It also has to stay *valid* while it is out of sight: the
          settings are all-or-nothing in the database (one check constraint, not four nullable
          columns), so the route validates this field in either mode.

          Which is what the second condition is for. The ceiling is the **duration**, and the
          duration is editable in either mode — so a host who shortens a sudden-death lot below
          their counter-bid time would otherwise be refused on a field they cannot see. It comes
          back exactly then, with its own `max` showing the bound.
        */}
        {value.closeMode === "standard" ||
        value.counterBidSeconds > value.durationSeconds ? (
          <NumberInput
            size="xs"
            label={t("lots.auction.counterLabel")}
            description={t("lots.auction.counterHelp")}
            value={value.counterBidSeconds}
            min={1}
            max={value.durationSeconds || 1800}
            allowDecimal={false}
            suffix="s"
            onChange={(next) =>
              onChange({ ...value, counterBidSeconds: Number(next) || 0 })
            }
            disabled={disabled}
            w={150}
          />
        ) : null}
      </Group>

      <Stack gap={4}>
        <Text size="xs" fw={500}>
          {t("lots.auction.modeLabel")}
        </Text>
        <SegmentedControl
          size="xs"
          value={value.closeMode}
          onChange={(next) =>
            onChange({ ...value, closeMode: next as AuctionSettings["closeMode"] })
          }
          disabled={disabled}
          data={[
            { value: "standard", label: t("lots.auction.mode.standard") },
            { value: "sudden", label: t("lots.auction.mode.sudden") },
          ]}
        />
        <Text size="xs" c="dimmed">
          {value.closeMode === "standard"
            ? t("lots.auction.mode.standardHelp", { seconds: value.counterBidSeconds })
            : t("lots.auction.mode.suddenHelp")}
        </Text>
      </Stack>
    </Stack>
  );
}
